import { useEffect, useState } from "react";

import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Grid, Paper } from "@mui/material";
import {
  CellClickedEvent,
  GridApi,
  GridReadyEvent,
  RowHeightParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { useTableData } from "../hooks/useTableData";
import {
  LadleRelining,
  PlugChange,
  PlugData,
} from "../models/LadleMaintenance";
import { DatePickerModal } from "./DatePickerModal";
import { DetailCellRenderer } from "./DetailCellRenderer";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
  deletePlugById,
  deleteReliningById,
} from "../repositories/LadleMaintenanceRepository";
import { defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

interface LadleMaintenanceTableProps {
  ladleId: number;
  isLoading: boolean;
  rows: LadleRelining[];
  refreshData: () => void;
}

export const LadleMaintenanceTable = ({
  ladleId,
  isLoading,
  rows,
  refreshData,
}: LadleMaintenanceTableProps): JSX.Element => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [showModal, setShowModal] = useState<
    "PLUG" | "RELINING" | "DELETEPLUG" | "DELETERELINING" | ""
  >("");
  const [timestamp, setTimestamp] = useState<Date | null>(null);
  const [id, setId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  const getRowHeight = (params: RowHeightParams): number => {
    return params.node.detail ? (params.data.Plugs?.length || 0) * 35 + 80 : 35;
  };

  const handleCellClicked = (params: CellClickedEvent): void => {
    let id = null;
    let timestamp = null;
    let showModal: "PLUG" | "RELINING" | "" = "";

    if (params.colDef.field === "PlugStart") {
      timestamp = params.data.PlugStart;
      id = params.data.PlugChangeId || null;
      showModal = "PLUG";
    } else if (params.colDef.field === "StartCampaign") {
      timestamp = params.data.StartCampaign;
      id = params.data.LadleReliningId || null;
      showModal = "RELINING";
    }

    if (id !== null && timestamp && showModal) {
      setId(id);
      setTimestamp(timestamp);
      setShowModal(showModal);
    }
  };

  const handleDetailCellClick = (data: {
    PlugStart: string;
    PlugChangeId?: number;
  }): void => {
    if (data.PlugChangeId) {
      setId(data.PlugChangeId);
      setTimestamp(new Date(data.PlugStart));
      setShowModal("PLUG");
    }
  };

  const handleEditRelining = (id: number): void => {
    let foundRelining: LadleRelining | undefined;

    rows.forEach((row) => {
      if (row.LadleReliningId === id) {
        foundRelining = row;
      }
    });

    if (foundRelining) {
      setId(foundRelining.LadleReliningId || null);
      setTimestamp(
        foundRelining.StartCampaign
          ? new Date(foundRelining.StartCampaign)
          : null,
      );
      setShowModal("RELINING");
    }
  };

  const handleEditPlug = (plugId: number): void => {
    let foundPlug: PlugChange | undefined;

    rows.forEach((row) => {
      if (row.Plugs && row.Plugs.length > 0) {
        const plug = row.Plugs.find((p) => p.PlugChangeId === plugId);
        if (plug) {
          foundPlug = plug;
        }
      }
    });

    if (foundPlug) {
      setId(foundPlug.PlugChangeId || null);
      setTimestamp(foundPlug.PlugStart ? new Date(foundPlug.PlugStart) : null);
      setShowModal("PLUG");
    }
  };

  const handleDeletePlug = (plugId: number): void => {
    setId(plugId);
    setShowModal("DELETEPLUG");
  };

  const handleDeleteRelining = (reliningId: number): void => {
    setId(reliningId);
    setShowModal("DELETERELINING");
  };

  const handleConfirmDelete = async (value?: string) => {
    setShowModal("");
    if (value !== "OK") return;
    if (id === null) return;

    let resp;
    if (showModal === "DELETEPLUG") {
      resp = await deletePlugById(id);
      if (resp.ok) {
        setNotificationMessage("Plug deleted successfully");
      } else {
        setError(resp.message || "Failed to delete plug");
      }
    } else if (showModal === "DELETERELINING") {
      resp = await deleteReliningById(id);
      if (resp.ok) {
        setNotificationMessage("Relining deleted successfully");
      } else {
        setError(resp.message || "Failed to delete relining");
      }
    }
    setId(null);
    refreshData();
  };

  const { masterColumnDefs, defaultColDef } = useTableData({
    handleEditRelining,
    handleDeleteRelining,
  });

  useEffect(() => {
    if (gridApi && rows.length > 0) {
      setTimeout(() => {
        const firstRowNode = gridApi.getDisplayedRowAtIndex(0);
        if (firstRowNode) {
          firstRowNode.setExpanded(true);
        }
      }, 100);
    }
  }, [gridApi, rows]);

  useEffect(() => {
    if (gridApi) {
      gridApi.resetRowHeights();
    }
  }, [rows, gridApi]);

  return (
    <>
      <Grid container>
        <Grid item md={12} xs={12}>
          <Paper
            style={{
              padding: 10,
              height: "calc(100vh - 280px)",
              minHeight: "calc(100vh - 280px)",
            }}
            elevation={3}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={masterColumnDefs}
              defaultColDef={defaultColDef}
              masterDetail
              isRowMaster={(dataItem: LadleRelining): boolean =>
                dataItem.Plugs?.length > 0
              }
              detailCellRenderer={(params: {
                data: { Plugs?: PlugData[] };
              }): JSX.Element => (
                <DetailCellRenderer
                  data={params.data}
                  onDetailCellClick={handleDetailCellClick}
                  deletePlug={handleDeletePlug}
                  editPlug={handleEditPlug}
                />
              )}
              getRowHeight={getRowHeight}
              suppressRowTransform
              loading={isLoading}
              onGridReady={(params: GridReadyEvent): void =>
                setGridApi(params.api)
              }
              getRowId={(params: { data: LadleRelining }): string =>
                `${params.data.LadleReliningId}`
              }
              onCellClicked={handleCellClicked}
              {...defaultGridOptions}
            />
          </Paper>
        </Grid>
      </Grid>

      <DatePickerModal
        showModal={
          showModal === "PLUG"
            ? "PLUG"
            : showModal === "RELINING"
              ? "RELINING"
              : ""
        }
        data={{
          id: id || 0,
          ladleId: ladleId,
          timestamp: timestamp,
        }}
        title={
          showModal === "PLUG"
            ? `CHANGE PLUG: ${dxtToLocalServerTime(timestamp, "MM/dd/yyyy HH:mm")}`
            : `CHANGE RELINING: ${dxtToLocalServerTime(timestamp, "MM/dd/yyyy HH:mm")}`
        }
        onHide={(): void => {
          refreshData();
          setShowModal("");
        }}
      />

      <ConfirmationDialogRaw
        title="Confirmation"
        message={`You will not be able to recover deleted data. Are you sure you want to proceed?`}
        open={showModal === "DELETEPLUG" || showModal === "DELETERELINING"}
        onClose={handleConfirmDelete}
      />

      <ErrorModal error={error} onHide={(): void => setError("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={(): void => setNotificationMessage("")}
      />
    </>
  );
};
